import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiPlus, FiEye, FiSearch, FiPaperclip, FiTrash2, FiEdit2 } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { toast, confirmDelete, showError } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import Pagination from '../../components/common/Pagination';
import StatusBadge from '../../components/common/StatusBadge';
import SearchableSelect from '../../components/common/SearchableSelect';
import { useAuth } from '../../context/AuthContext';
import { formatMoney, formatDate } from '../../utils/format';

const TYPE_LABEL = {
  general_voucher: 'General Voucher',
  bank_payment: 'Bank Payment',
  bank_receipt: 'Bank Receipt',
  cash_payment: 'Cash Payment',
  cash_receipt: 'Cash Receipt',
};

export default function Vouchers() {
  const { hasPermission } = useAuth();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [params, setParams] = useState({ page: 1, search: '', voucher_type: '', status: '', per_page: 100 });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.vouchers.list(params);
      const list = Array.isArray(data.data) ? data.data : data.vouchers || data;
      setRows(Array.isArray(list) ? list : list.data || []);
      setMeta(data.data ? { current_page: data.current_page, last_page: data.last_page, total: data.total, per_page: data.per_page, per_page_options: data.data.per_page_options } : null);
    } catch (err) {
      showError('Failed to load vouchers', extractError(err));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  const downloadAttachment = async (id, index, meta) => {
    try {
      const res = await api.vouchers.attachment(id, index);
      const blob = new Blob([res.data], { type: meta.mime_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = meta.file_name || `attachment-${index + 1}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      showError('Failed to download attachment', extractError(err));
    }
  };

  const handleDelete = async (row) => {
    const res = await confirmDelete(`voucher ${row.voucher_no}`);
    if (!res.isConfirmed) return;
    try {
      await api.vouchers.remove(row.id);
      toast('success', '', 'Voucher deleted');
      load();
    } catch (err) {
      showError('Failed to delete voucher', extractError(err));
    }
  };

  return (
    <div className="page">
      <PageHeader
        title="Vouchers"
        subtitle="General, bank and cash vouchers."
        crumbs={[{ label: 'Finance' }, { label: 'Vouchers' }]}
        actions={hasPermission('create-vouchers') && (
          <Link to="/vouchers/new" className="btn btn-primary"><FiPlus /> New Voucher</Link>
        )}
      />

      <div className="card">
        <div className="card-body">
          <div className="filters-bar">
            <div className="topbar-search">
              <FiSearch className="search-icon" />
              <input placeholder="Search voucher no or narration..." value={params.search} onChange={(e) => setParams({ ...params, search: e.target.value, page: 1 })} />
            </div>
            <SearchableSelect
              options={[{ value: '', label: 'All Types' }, ...Object.entries(TYPE_LABEL).map(([k, v]) => ({ value: k, label: v }))]}
              value={params.voucher_type}
              onChange={(v) => setParams({ ...params, voucher_type: v, page: 1 })}
              placeholder="All Types"
            />
            <SearchableSelect
              options={[{ value: '', label: 'All Status' }, { value: 'draft', label: 'Draft' }, { value: 'posted', label: 'Posted' }, { value: 'void', label: 'Void' }]}
              value={params.status}
              onChange={(v) => setParams({ ...params, status: v, page: 1 })}
              placeholder="All Status"
            />
          </div>

          <DataTable
            loading={loading}
            data={rows}
            emptyTitle="No vouchers found"
            columns={[
              { key: 'voucher_no', label: 'Voucher No', render: (r) => <b>{r.voucher_no}</b> },
              { key: 'date', label: 'Date', render: (r) => formatDate(r.date) },
              { key: 'voucher_type', label: 'Type', render: (r) => <StatusBadge status={r.voucher_type} label={TYPE_LABEL[r.voucher_type] || r.voucher_type} /> },
              { key: 'narration', label: 'Narration', render: (r) => (r.narration ? String(r.narration).toUpperCase() : '-') },
              { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
              { key: 'attachments', label: 'Attachments', render: (r) => (
                (r.attachments_meta && r.attachments_meta.length) ? (
                  <div className="actions" style={{ opacity: 1 }}>
                    {r.attachments_meta.map((m, i) => (
                      <button key={i} className="btn-icon" title={m.file_name} onClick={() => downloadAttachment(r.id, i, m)}>
                        <FiPaperclip />
                      </button>
                    ))}
                  </div>
                ) : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>
              ) },
              { key: 'total', label: 'Amount', align: 'right', render: (r) => formatMoney(r.total_debit || r.amount) },
              { key: 'id', label: '', align: 'right', render: (r) => (
                <div className="actions">
                  <Link to={`/vouchers/${r.id}`} className="btn-icon" title="View"><FiEye /></Link>
                  {hasPermission('create-vouchers') && r.status !== 'void' && (
                    <Link to={`/vouchers/${r.id}/edit`} className="btn-icon" title="Edit"><FiEdit2 /></Link>
                  )}
                  {hasPermission('delete-vouchers') && r.status !== 'posted' && (
                    <button className="btn-icon danger" title="Delete" onClick={() => handleDelete(r)}><FiTrash2 /></button>
                  )}
                </div>
              ) },
            ]}
          />
          {meta && <Pagination meta={meta} onPage={(p) => setParams({ ...params, page: p })} onPerPage={(n) => setParams({ ...params, per_page: n, page: 1 })} />}
        </div>
      </div>
    </div>
  );
}